import {
  SAFETY_CATEGORIES,
  SAFETY_CATEGORY_LABELS,
  type SafetyCategory,
  type SafetyEmployeeCard,
  type SafetyRecordItem,
  type SafetyRequirementItem,
  type SafetyState,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { Check, ChevronLeft, Plus, Printer, Search } from 'lucide-react';
import { useRef, useState } from 'react';
import { EmptyPanel, Pager } from '@/components/crm-bits';
import { Dialog } from '@/components/dialog';
import {
  DueText,
  FormError,
  type RecordDefaults,
  RecordDialog,
  RecordFormDialog,
  RecordsTable,
  SafetyNav,
  StateBadge,
} from '@/components/safety-bits';
import { Button, ErrorBox, Field, Input, PageHeader, Segmented, Select, Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import { useCan } from '@/lib/auth';
import { formatDay, plural } from '@/lib/format';
import { departmentsTreeQuery, flattenTree } from '@/lib/queries';
import {
  safetyEmployeeQuery,
  safetyMetaQuery,
  safetyMyQuery,
  safetyRecordsQuery,
  safetyStatusQuery,
  signRecord,
} from '@/lib/safety';

const useStaff = () => {
  const can = useCan();
  return { staff: can('safety.read_all') || can('safety.manage'), manage: can('safety.manage') };
};

function CategorySelect({
  value,
  onChange,
}: {
  value: SafetyCategory | undefined;
  onChange: (v: SafetyCategory | undefined) => void;
}) {
  return (
    <Select
      aria-label="Раздел"
      value={value ?? ''}
      onChange={(e) => onChange((e.target.value || undefined) as SafetyCategory | undefined)}
      className="w-full sm:w-[170px]"
    >
      <option value="">Всё</option>
      {SAFETY_CATEGORIES.map((c) => (
        <option key={c} value={c}>
          {SAFETY_CATEGORY_LABELS[c]}
        </option>
      ))}
    </Select>
  );
}

function DepartmentSelect({
  value,
  onChange,
}: {
  value?: string;
  onChange: (v: string | undefined) => void;
}) {
  const tree = useQuery(departmentsTreeQuery);
  return (
    <Select
      aria-label="Отдел"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || undefined)}
      className="w-full sm:w-[220px]"
    >
      <option value="">Все отделы</option>
      {flattenTree(tree.data ?? []).map(({ node, depth }) => (
        <option key={node.id} value={node.id}>
          {`${'  '.repeat(depth)}${node.name}`}
        </option>
      ))}
    </Select>
  );
}

function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value?: string;
  onChange: (v?: string) => void;
  placeholder: string;
}) {
  const [q, setQ] = useState(value ?? '');
  const debounce = useRef<number | undefined>(undefined);
  return (
    <div className="relative w-full sm:max-w-[260px]">
      <Search
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-steel"
        aria-hidden
      />
      <Input
        type="search"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          window.clearTimeout(debounce.current);
          const v = e.target.value.trim() || undefined;
          debounce.current = window.setTimeout(() => onChange(v), 250);
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        className="pl-9"
      />
    </div>
  );
}

// ---------- Сводка ----------

export interface SafetySearch {
  state?: 'attention' | 'overdue' | 'missing' | 'soon' | 'ok' | 'exempt' | 'all';
  category?: SafetyCategory;
  department?: string;
  q?: string;
}

const STATE_FILTERS: Record<NonNullable<SafetySearch['state']>, SafetyState[]> = {
  attention: ['overdue', 'missing', 'soon'],
  overdue: ['overdue'],
  missing: ['missing'],
  soon: ['soon'],
  ok: ['ok'],
  exempt: ['exempt'],
  all: ['overdue', 'missing', 'soon', 'ok', 'exempt'],
};

export function SafetyStatusPage() {
  const search = useSearch({ strict: false }) as SafetySearch;
  const navigate = useNavigate();
  const { staff, manage } = useStaff();
  const [form, setForm] = useState<RecordDefaults | null>(null);
  const state = search.state ?? 'attention';
  const q = useQuery({
    ...safetyStatusQuery({
      state: STATE_FILTERS[state],
      category: search.category,
      departmentId: search.department,
      q: search.q,
    }),
    enabled: staff,
  });
  if (!staff) return <Navigate to="/safety/my" replace />;
  const setSearch = (patch: Partial<SafetySearch>) =>
    navigate({ to: '/safety', search: { ...search, ...patch }, replace: true });
  const c = q.data?.counters;
  const attention = c ? c.overdue + c.missing + c.soon : 0;

  return (
    <div>
      <PageHeader
        title="Охрана труда"
        description={
          c
            ? attention
              ? `Требуют внимания: просрочено ${c.overdue}, не проходили ${c.missing}, скоро срок ${c.soon}`
              : 'Все инструктажи и медосмотры в срок'
            : 'Инструктажи, обучение и медосмотры'
        }
        actions={
          manage && (
            <Button onClick={() => setForm({})}>
              <Plus className="size-4" aria-hidden />
              Запись
            </Button>
          )
        }
      />
      <SafetyNav />
      {form && <RecordFormDialog defaults={form} onClose={() => setForm(null)} />}
      {c && c.unsigned > 0 && (
        <p className="mb-4 text-[13.5px] text-steel">
          Ждут подтверждения сотрудников: {c.unsigned}.{' '}
          <Link to="/safety/journal" search={{ unsigned: true }} className="link">
            Показать
          </Link>
        </p>
      )}
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center">
        <div className="min-w-0 max-w-full">
          <Segmented
            label="Состояние"
            value={state}
            onChange={(v) => setSearch({ state: v === 'attention' ? undefined : v })}
            options={[
              { value: 'attention', label: c ? `Внимание · ${attention}` : 'Внимание' },
              { value: 'overdue', label: c?.overdue ? `Просрочено · ${c.overdue}` : 'Просрочено' },
              { value: 'missing', label: c?.missing ? `Не проходили · ${c.missing}` : 'Не проходили' },
              { value: 'soon', label: c?.soon ? `Скоро · ${c.soon}` : 'Скоро' },
              { value: 'ok', label: 'В порядке' },
              { value: 'all', label: 'Все' },
            ]}
          />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <CategorySelect value={search.category} onChange={(v) => setSearch({ category: v })} />
          <DepartmentSelect value={search.department} onChange={(v) => setSearch({ department: v })} />
          <SearchBox value={search.q} onChange={(v) => setSearch({ q: v })} placeholder="Фамилия" />
        </div>
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <ErrorBox error={q.error} />
      ) : q.data.items.length === 0 ? (
        <EmptyPanel title={state === 'attention' ? 'Всё в порядке' : 'Ничего не найдено'}>
          {state === 'attention'
            ? 'Нет просроченных, приближающихся сроков и тех, кто не проходил.'
            : 'Измените фильтры.'}
        </EmptyPanel>
      ) : (
        <RequirementsTable
          items={q.data.items}
          onAdd={
            manage
              ? (r) =>
                  setForm({
                    kindId: r.kind.id,
                    employees: [{ id: r.employee.id, fullName: r.employee.fullName, hint: null }],
                  })
              : undefined
          }
        />
      )}
    </div>
  );
}

function RequirementsTable({
  items,
  onAdd,
  hideEmployee,
  extra,
}: {
  items: SafetyRequirementItem[];
  onAdd?: (r: SafetyRequirementItem) => void;
  hideEmployee?: boolean;
  extra?: (r: SafetyRequirementItem) => React.ReactNode;
}) {
  return (
    <div className="panel relative overflow-x-auto">
      <table className="w-full min-w-[680px] text-left">
        <thead>
          <tr className="border-b border-line text-[13px] text-steel">
            {!hideEmployee && (
              <th scope="col" className="px-4 py-2.5 font-medium">
                Сотрудник
              </th>
            )}
            <th scope="col" className="px-4 py-2.5 font-medium">
              Что пройти
            </th>
            <th scope="col" className="px-4 py-2.5 font-medium">
              Последний раз
            </th>
            <th scope="col" className="px-4 py-2.5 font-medium">
              Срок
            </th>
            <th scope="col" className="px-4 py-2.5 font-medium">
              Состояние
            </th>
            {(onAdd || extra) && (
              <th scope="col" className="px-4 py-2.5">
                <span className="sr-only">Действия</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {items.map((r) => (
            <tr key={`${r.employee.id}:${r.kind.id}`} className="hover:bg-paper/70">
              {!hideEmployee && (
                <td className="px-4 py-2.5">
                  <Link
                    to="/safety/employees/$id"
                    params={{ id: r.employee.id }}
                    className="font-medium hover:text-link"
                  >
                    {r.employee.fullName}
                  </Link>
                  <span className="block text-[12.5px] text-steel">
                    {[r.employee.positionName, r.employee.departmentName].filter(Boolean).join(' · ') || ' '}
                  </span>
                </td>
              )}
              <td className="px-4 py-2.5">
                {r.kind.name}
                <span className="block text-[12.5px] text-steel">
                  {r.kind.periodMonths ? `раз в ${periodText(r.kind.periodMonths)}` : 'разово'}
                </span>
              </td>
              <td className="px-4 py-2.5 whitespace-nowrap">{r.lastDate ? formatDay(r.lastDate) : '—'}</td>
              <td className="px-4 py-2.5 whitespace-nowrap">
                <DueText item={r} />
              </td>
              <td className="px-4 py-2.5 whitespace-nowrap">
                <StateBadge state={r.state} />
                {r.state === 'exempt' && r.exemptReason && (
                  <span className="block text-[12.5px] text-steel">{r.exemptReason}</span>
                )}
              </td>
              {(onAdd || extra) && (
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  {extra?.(r)}
                  {onAdd && r.state !== 'exempt' && (
                    <Button size="sm" variant="secondary" onClick={() => onAdd(r)}>
                      Внести
                    </Button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function periodText(months: number) {
  if (months % 12 === 0) {
    const y = months / 12;
    return y === 1 ? 'год' : `${y} ${plural(y, ['год', 'года', 'лет'])}`;
  }
  return months === 1 ? 'месяц' : `${months} ${plural(months, ['месяц', 'месяца', 'месяцев'])}`;
}

// ---------- Журнал ----------

export interface SafetyJournalSearch {
  category?: SafetyCategory;
  kind?: string;
  department?: string;
  from?: string;
  to?: string;
  unsigned?: boolean;
  q?: string;
  page?: number;
}

export function SafetyJournalPage() {
  const search = useSearch({ strict: false }) as SafetyJournalSearch;
  const navigate = useNavigate();
  const { staff, manage } = useStaff();
  const meta = useQuery(safetyMetaQuery);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<SafetyRecordItem | null>(null);
  const list = useQuery({
    ...safetyRecordsQuery({
      category: search.category,
      kindId: search.kind,
      departmentId: search.department,
      from: search.from,
      to: search.to,
      unsigned: search.unsigned,
      q: search.q,
      page: search.page ?? 1,
    }),
    enabled: staff,
  });
  if (!staff) return <Navigate to="/safety/my" replace />;
  const setSearch = (patch: Partial<SafetyJournalSearch>) =>
    navigate({ to: '/safety/journal', search: { ...search, page: undefined, ...patch }, replace: true });
  const kinds = (meta.data?.kinds ?? []).filter((k) => !search.category || k.category === search.category);
  const printCategory = search.category ?? meta.data?.kinds.find((k) => k.id === search.kind)?.category;

  return (
    <div>
      <PageHeader
        title="Журнал"
        description="Записи о прохождении инструктажей, обучения и медосмотров"
        actions={
          <>
            <Link
              to="/safety/print"
              search={{
                category: printCategory ?? 'instruction',
                kind: search.kind,
                department: search.department,
                from: search.from,
                to: search.to,
              }}
              target="_blank"
              className="inline-flex h-9 items-center gap-2 rounded-[var(--radius-control)] border border-line-strong bg-surface px-4 text-[14px] font-medium hover:bg-sunken"
            >
              <Printer className="size-4" aria-hidden />
              Печать
            </Link>
            {manage && (
              <Button onClick={() => setCreating(true)}>
                <Plus className="size-4" aria-hidden />
                Запись
              </Button>
            )}
          </>
        }
      />
      <SafetyNav />
      {creating && <RecordFormDialog onClose={() => setCreating(false)} />}
      {open && <RecordDialog record={open} onClose={() => setOpen(null)} />}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <CategorySelect
          value={search.category}
          onChange={(v) => setSearch({ category: v, kind: undefined })}
        />
        <Select
          aria-label="Вид"
          value={search.kind ?? ''}
          onChange={(e) => setSearch({ kind: e.target.value || undefined })}
          className="w-full sm:w-[260px]"
        >
          <option value="">Все виды</option>
          {kinds.map((k) => (
            <option key={k.id} value={k.id}>
              {k.name}
            </option>
          ))}
        </Select>
        <DepartmentSelect value={search.department} onChange={(v) => setSearch({ department: v })} />
        <div className="flex gap-2">
          <Input
            type="date"
            aria-label="С даты"
            value={search.from ?? ''}
            onChange={(e) => setSearch({ from: e.target.value || undefined })}
            className="w-full sm:w-[150px]"
          />
          <Input
            type="date"
            aria-label="По дату"
            value={search.to ?? ''}
            onChange={(e) => setSearch({ to: e.target.value || undefined })}
            className="w-full sm:w-[150px]"
          />
        </div>
        <SearchBox
          value={search.q}
          onChange={(v) => setSearch({ q: v })}
          placeholder="Фамилия или № документа"
        />
        <label className="inline-flex h-9 items-center gap-2 text-[13.5px]">
          <input
            type="checkbox"
            checked={Boolean(search.unsigned)}
            onChange={(e) => setSearch({ unsigned: e.target.checked || undefined })}
            className="size-4 accent-graphite"
          />
          Ждут подтверждения
        </label>
      </div>
      {list.isPending ? (
        <Spinner />
      ) : list.error ? (
        <ErrorBox error={list.error} />
      ) : list.data.items.length === 0 ? (
        <EmptyPanel title="Записей нет">Измените фильтры или внесите запись.</EmptyPanel>
      ) : (
        <>
          <RecordsTable items={list.data.items} onOpen={setOpen} />
          <Pager
            page={list.data.page}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => navigate({ to: '/safety/journal', search: { ...search, page: p }, replace: true })}
          />
        </>
      )}
    </div>
  );
}

// ---------- Мои ----------

export function SafetyMyPage() {
  const q = useQuery(safetyMyQuery);
  const conduct = useQuery(safetyRecordsQuery({ pageSize: 200 }));
  const qc = useQueryClient();
  const [open, setOpen] = useState<SafetyRecordItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sign = useMutation({
    mutationFn: (v: { id: string; as: 'employee' | 'conductor' }) => signRecord(v.id, v.as),
    onSuccess: () => setError(null),
    onError: (err) => setError(err.message),
    onSettled: () => qc.invalidateQueries({ queryKey: ['safety'] }),
  });
  const toConduct = (conduct.data?.items ?? []).filter((r) => r.canSignAsConductor);

  return (
    <div>
      <PageHeader title="Охрана труда" description="Мои инструктажи, обучение и медосмотры" />
      <SafetyNav />
      {open && <RecordDialog record={open} onClose={() => setOpen(null)} />}
      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <ErrorBox error={q.error} />
      ) : (
        <div className="flex flex-col gap-6">
          {error && <FormError>{error}</FormError>}
          <ToSign
            title="Подтвердите прохождение"
            hint="Нажимая «Ознакомлен», вы подтверждаете, что прошли инструктаж и поняли его. Это ваша подпись в журнале."
            items={q.data.records.filter((r) => r.canSignAsEmployee)}
            label="Ознакомлен, прошёл"
            onSign={(id) => sign.mutate({ id, as: 'employee' })}
            pending={sign.isPending}
          />
          <ToSign
            title="Подтвердите проведение"
            hint="Вы указаны как проводивший — подтвердите, что провели."
            items={toConduct}
            label="Провёл"
            onSign={(id) => sign.mutate({ id, as: 'conductor' })}
            pending={sign.isPending}
            showEmployee
          />
          <section>
            <h2 className="mb-2 text-[16px] font-semibold">Что нужно проходить</h2>
            {q.data.requirements.length ? (
              <RequirementsTable items={q.data.requirements} hideEmployee />
            ) : (
              <EmptyPanel title="Требований нет" />
            )}
          </section>
          <section>
            <h2 className="mb-2 text-[16px] font-semibold">История</h2>
            {q.data.records.length ? (
              <RecordsTable items={q.data.records} onOpen={setOpen} hideEmployee />
            ) : (
              <EmptyPanel title="Записей пока нет" />
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function ToSign({
  title,
  hint,
  items,
  label,
  onSign,
  pending,
  showEmployee,
}: {
  title: string;
  hint: string;
  items: SafetyRecordItem[];
  label: string;
  onSign: (id: string) => void;
  pending: boolean;
  showEmployee?: boolean;
}) {
  if (!items.length) return null;
  return (
    <section className="panel border-signal/60 bg-signal-soft/40 px-4 py-4">
      <h2 className="text-[16px] font-semibold">{title}</h2>
      <p className="mt-1 mb-3 max-w-[70ch] text-[13.5px] text-steel">{hint}</p>
      <ul className="divide-y divide-line">
        {items.map((r) => (
          <li
            key={r.id}
            className="flex flex-col gap-2 py-2.5 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <p className="font-medium">
                {r.kind.name}
                {showEmployee && <span className="font-normal text-steel"> — {r.employee.fullName}</span>}
              </p>
              <p className="text-[13px] text-steel">
                {formatDay(r.date)}
                {r.reason && ` · ${r.reason}`}
                {r.conductedBy && !showEmployee && ` · проводил ${r.conductedBy.fullName}`}
              </p>
            </div>
            <Button
              size="sm"
              loading={pending}
              onClick={() => onSign(r.id)}
              className="self-start sm:self-auto"
            >
              <Check className="size-4" aria-hidden />
              {label}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------- Сотрудник ----------

export function SafetyEmployeePage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const q = useQuery(safetyEmployeeQuery(id));
  const [form, setForm] = useState<RecordDefaults | null>(null);
  const [open, setOpen] = useState<SafetyRecordItem | null>(null);
  const [exempt, setExempt] = useState<SafetyRequirementItem | null>(null);
  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  const card = q.data;
  const e = card.employee;
  const me = { id: e.id, fullName: e.fullName, hint: null };
  return (
    <div>
      <Link
        to="/safety"
        className="mb-3 inline-flex items-center gap-1 text-[13.5px] text-steel hover:text-ink"
      >
        <ChevronLeft className="size-4" aria-hidden />
        Сводка
      </Link>
      <PageHeader
        title={e.fullName}
        description={[e.positionName, e.departmentName, e.hireDate && `принят ${formatDay(e.hireDate)}`]
          .filter(Boolean)
          .join(' · ')}
        actions={
          card.canManage && (
            <Button onClick={() => setForm({ employees: [me] })}>
              <Plus className="size-4" aria-hidden />
              Запись
            </Button>
          )
        }
      />
      {form && <RecordFormDialog defaults={form} onClose={() => setForm(null)} />}
      {open && <RecordDialog record={open} onClose={() => setOpen(null)} />}
      {exempt && <ExemptDialog card={card} item={exempt} onClose={() => setExempt(null)} />}
      <div className="flex flex-col gap-6">
        <section>
          <h2 className="mb-2 text-[16px] font-semibold">Требования</h2>
          {card.requirements.length ? (
            <RequirementsTable
              items={card.requirements}
              hideEmployee
              onAdd={card.canManage ? (r) => setForm({ kindId: r.kind.id, employees: [me] }) : undefined}
              extra={
                card.canManage
                  ? (r) => (
                      <button type="button" className="link mr-3 text-[13px]" onClick={() => setExempt(r)}>
                        {r.state === 'exempt' ? 'Вернуть' : 'Не требуется'}
                      </button>
                    )
                  : undefined
              }
            />
          ) : (
            <EmptyPanel title="Требований нет">
              Для должности не назначено ни одного вида — настройте в «Настройках».
            </EmptyPanel>
          )}
        </section>
        <section>
          <h2 className="mb-2 text-[16px] font-semibold">История</h2>
          {card.records.length ? (
            <RecordsTable items={card.records} onOpen={setOpen} hideEmployee />
          ) : (
            <EmptyPanel title="Записей пока нет" />
          )}
        </section>
      </div>
    </div>
  );
}

function ExemptDialog({
  card,
  item,
  onClose,
}: {
  card: SafetyEmployeeCard;
  item: SafetyRequirementItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const exempt = item.state !== 'exempt';
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () =>
      api(`/safety/employees/${card.employee.id}/exemptions`, {
        method: 'PUT',
        body: { kindId: item.kind.id, exempt, reason: reason.trim() || null },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['safety'] });
      onClose();
    },
  });
  return (
    <Dialog title={exempt ? 'Не требуется' : 'Вернуть требование'} onClose={onClose} width={520}>
      <form
        className="flex flex-col gap-4 px-5 py-5"
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <p>
          {exempt
            ? `«${item.kind.name}» не будет требоваться у сотрудника ${card.employee.fullName}, напоминаний не будет.`
            : `«${item.kind.name}» снова требуется у сотрудника ${card.employee.fullName}.`}
        </p>
        {exempt && (
          <Field label="Основание" hint="Например: освобождён приказом №… (работа только с ПК)">
            {(p) => <Input {...p} value={reason} onChange={(e) => setReason(e.target.value)} />}
          </Field>
        )}
        {m.error && <FormError>{m.error.message}</FormError>}
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" loading={m.isPending}>
            {exempt ? 'Отметить' : 'Вернуть'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------- Печать журнала ----------

export interface SafetyPrintSearch {
  category?: SafetyCategory;
  kind?: string;
  department?: string;
  from?: string;
  to?: string;
}

const PRINT_TITLES: Record<SafetyCategory, string> = {
  instruction: 'Журнал регистрации инструктажей по охране труда',
  training: 'Журнал учёта обучения и проверки знаний по охране труда',
  medical: 'Журнал учёта медицинских осмотров',
};

const signedText = (at: string | null) =>
  at
    ? `Подтверждено в системе ${new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(at))}`
    : '';

export function SafetyPrintPage() {
  const search = useSearch({ strict: false }) as SafetyPrintSearch;
  const category = search.category ?? 'instruction';
  const meta = useQuery(safetyMetaQuery);
  const tree = useQuery(departmentsTreeQuery);
  const list = useQuery(
    safetyRecordsQuery({
      category,
      kindId: search.kind,
      departmentId: search.department,
      from: search.from,
      to: search.to,
      pageSize: 2000,
      order: 'asc',
    }),
  );
  const company = tree.data?.[0]?.name ?? '';
  const kindName = meta.data?.kinds.find((k) => k.id === search.kind)?.name;
  const department = search.department
    ? flattenTree(tree.data ?? []).find((n) => n.node.id === search.department)?.node.name
    : null;
  const period =
    search.from || search.to
      ? `${search.from ? `с ${formatDay(search.from)}` : ''} ${search.to ? `по ${formatDay(search.to)}` : ''}`.trim()
      : null;
  const cell = 'border border-ink/60 px-1.5 py-1 align-top';
  const items = list.data?.items ?? [];

  return (
    <div className="min-h-dvh bg-white px-4 py-6 text-[12px] text-black print:p-0">
      <style>{'@page { size: A4 landscape; margin: 12mm; }'}</style>
      <div className="mb-4 flex flex-wrap gap-2 print:hidden">
        <Button onClick={() => window.print()}>
          <Printer className="size-4" aria-hidden />
          Печать
        </Button>
        <Select
          aria-label="Журнал"
          value={category}
          onChange={(e) => {
            const url = new URL(window.location.href);
            url.searchParams.set('category', e.target.value);
            url.searchParams.delete('kind');
            window.location.href = url.toString();
          }}
          className="w-[260px]"
        >
          {SAFETY_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {PRINT_TITLES[c]}
            </option>
          ))}
        </Select>
        <p className="self-center text-[13px] text-steel">
          Подписи тех, кто подтвердил в системе, напечатаются отметкой; остальным — расписаться в графе.
        </p>
      </div>
      <header className="mb-3 text-center">
        <p>{company}</p>
        <h1 className="mt-1 text-[16px] font-semibold">{PRINT_TITLES[category]}</h1>
        <p className="mt-0.5">{[kindName, department, period].filter(Boolean).join(' · ')}</p>
      </header>
      {list.isPending ? (
        <Spinner />
      ) : list.error ? (
        <ErrorBox error={list.error} />
      ) : (
        <div className="overflow-x-auto print:overflow-visible">
          <table className="w-full min-w-[900px] border-collapse print:min-w-0">
            <thead>
              {category === 'medical' ? (
                <tr>
                  {[
                    '№',
                    'Дата',
                    'ФИО',
                    'Должность',
                    'Вид осмотра',
                    'Медицинская организация',
                    'Результат',
                    '№ заключения',
                    'Следующий',
                  ].map((h) => (
                    <th key={h} className={clsx(cell, 'font-semibold')}>
                      {h}
                    </th>
                  ))}
                </tr>
              ) : (
                <tr>
                  {[
                    '№',
                    'Дата',
                    category === 'instruction' ? 'ФИО инструктируемого' : 'ФИО',
                    'Профессия, должность',
                    category === 'instruction' ? 'Вид инструктажа' : 'Программа обучения',
                    category === 'instruction' ? 'Причина внепланового / целевого' : 'Результат, № протокола',
                    category === 'instruction' ? 'ФИО, должность инструктирующего' : 'Кто проводил',
                    category === 'instruction' ? 'Подпись инструктирующего' : 'Подпись проводившего',
                    category === 'instruction' ? 'Подпись инструктируемого' : 'Подпись обучаемого',
                  ].map((h) => (
                    <th key={h} className={clsx(cell, 'font-semibold')}>
                      {h}
                    </th>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td className={cell} colSpan={9}>
                    Записей нет
                  </td>
                </tr>
              )}
              {items.map((r, i) =>
                category === 'medical' ? (
                  <tr key={r.id}>
                    <td className={cell}>{i + 1}</td>
                    <td className={clsx(cell, 'whitespace-nowrap')}>
                      {r.date.split('-').reverse().join('.')}
                    </td>
                    <td className={cell}>{r.employee.fullName}</td>
                    <td className={cell}>{r.employee.positionName}</td>
                    <td className={cell}>{r.kind.name}</td>
                    <td className={cell}>{r.conductedByName ?? r.conductedBy?.fullName}</td>
                    <td className={cell}>
                      {r.result === 'passed'
                        ? 'Годен'
                        : r.result === 'limited'
                          ? 'Годен с ограничениями'
                          : 'Не годен'}
                    </td>
                    <td className={cell}>{r.documentNo}</td>
                    <td className={clsx(cell, 'whitespace-nowrap')}>
                      {r.nextDueAt?.split('-').reverse().join('.')}
                    </td>
                  </tr>
                ) : (
                  <tr key={r.id} className="h-10">
                    <td className={cell}>{i + 1}</td>
                    <td className={clsx(cell, 'whitespace-nowrap')}>
                      {r.date.split('-').reverse().join('.')}
                    </td>
                    <td className={cell}>{r.employee.fullName}</td>
                    <td className={cell}>{r.employee.positionName}</td>
                    <td className={cell}>{r.kind.name}</td>
                    <td className={cell}>
                      {category === 'instruction'
                        ? r.reason
                        : [
                            r.result === 'failed' ? 'Не пройдена' : 'Пройдена',
                            r.documentNo && `№ ${r.documentNo}`,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                    </td>
                    <td className={cell}>
                      {[
                        r.conductedBy &&
                          [r.conductedBy.fullName, r.conductedBy.positionName].filter(Boolean).join(', '),
                        r.conductedByName,
                      ]
                        .filter(Boolean)
                        .join('; ')}
                    </td>
                    <td className={clsx(cell, 'text-[10px] text-ink-soft')}>
                      {signedText(r.conductorSignedAt)}
                    </td>
                    <td className={clsx(cell, 'text-[10px] text-ink-soft')}>
                      {signedText(r.employeeSignedAt)}
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
